import React, { useEffect, useState } from "react";
import { Row, Col, Input, Select, Switch, Collapse, Spin, Checkbox, message } from "antd";
import { CheckCircleFilled, EnvironmentOutlined, GlobalOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { apiRequest } from "../../../../../utilities/apiMethod";
import { useVendorMasterStore } from "../../../../../store/useVendorMasterStore";
import { useGenralInfoFormStore } from "../../../../../store/vendorRegistrationStore/useGeneralInfoFormStore";
import { useThemeStore } from "../../../../../store/useThemeStore";
import ImagePreviewModal from "../../../../IRNRegister/ListRegister/imageModal";
import ApproveReject from "../../../../../components/approveReject/ApproveReject";
import { getTradeTypeApi, panValidationApi } from "../../../../../services/vendorMaster";

/* ── Helpers ────────────────────────────────────────────────────────────────── */
function hexToRgb(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r},${g},${b}`;
}

/* Uppercase label */
function FL({ children, required }) {
  return (
    <div style={{ fontSize: 10, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
      {children}{required && <span style={{ color: "#ef4444", marginLeft: 2 }}>*</span>}
    </div>
  );
}

/* Inline field error */
function FE({ error }) {
  if (!error) return null;
  return <p style={{ margin: "3px 0 0", fontSize: 11, color: "#dc2626" }}>{error}</p>;
}

/* ── Reusable address block ─────────────────────────────────────────────────── */
function AddressBlock({
  type, label, required,
  copyable, copyChecked, onCopyChange,
  showApprovalCheck, approvalChecked, onApprovalChange, approveEnabled,
  disabled, themeColor,
  country, state,
  getAddressValue, setAddressField, getCity, errors,
}) {
  const rgb = hexToRgb(themeColor);

  const extra = (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }} onClick={e => e.stopPropagation()}>
      {copyable && !disabled && (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 11, color: "#9ca3af" }}>Copy from Registered HO</span>
          <Switch size="small" checked={copyChecked} onChange={onCopyChange} />
        </div>
      )}
      {showApprovalCheck && (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 11, color: "#6b7280", fontWeight: 600 }}>Approved</span>
          <Checkbox
            checked={approvalChecked}
            disabled={!approveEnabled}
            onChange={() => onApprovalChange?.()}
          />
        </div>
      )}
    </div>
  );

  return (
    <Collapse
      style={{ marginTop: 10, borderRadius: 10, overflow: "hidden", border: `1px solid #f0f1f3` }}
      defaultActiveKey={required ? ["1"] : []}
      items={[{
        key: "1",
        label: (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <EnvironmentOutlined style={{ color: required ? themeColor : "#9ca3af", fontSize: 13 }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>{label}</span>
            {required && (
              <span style={{ fontSize: 10, fontWeight: 700, color: themeColor, background: `rgba(${rgb},0.10)`, padding: "1px 8px", borderRadius: 20 }}>
                Required
              </span>
            )}
          </div>
        ),
        extra,
        children: (
          <div style={{ padding: "4px 0" }}>
            <Row gutter={[12, 0]}>
              <Col xl={6} md={12} sm={24}>
                <FL>Country{required && " *"}</FL>
                <Select
                  placeholder="Select Country" allowClear showSearch optionFilterProp="label"
                  style={{ width: "100%", height: 34 }}
                  options={country?.map(c => ({ label: c.CountryName, value: c.CountryCode }))}
                  value={getAddressValue(type, "country")}
                  onChange={v => setAddressField(type, "country", v)}
                  disabled={disabled}
                />
                {required && <FE error={errors?.country} />}
              </Col>
              <Col xl={6} md={12} sm={24}>
                <FL>State{required && " *"}</FL>
                <Select
                  placeholder="Select State" allowClear showSearch optionFilterProp="label"
                  style={{ width: "100%", height: 34 }}
                  options={state?.map(s => ({ label: s.StateName, value: s.StateCode }))}
                  value={getAddressValue(type, "state")}
                  onChange={v => { setAddressField(type, "state", v); getCity(); }}
                  disabled={disabled}
                />
                {required && <FE error={errors?.state} />}
              </Col>
              <Col xl={6} md={12} sm={24}>
                <FL>City{required && " *"}</FL>
                <Input
                  placeholder="Enter City" style={{ borderRadius: 7, height: 34, textTransform: "uppercase" }}
                  value={getAddressValue(type, "city")}
                  onChange={e => { if (/^[A-Za-z\s]*$/.test(e.target.value)) setAddressField(type, "city", e.target.value); }}
                  disabled={disabled}
                />
                {required && <FE error={errors?.city} />}
              </Col>
              <Col xl={6} md={12} sm={24}>
                <FL>Pin Code{required && " *"}</FL>
                <Input
                  placeholder="Enter Pin Code" style={{ borderRadius: 7, height: 34 }}
                  value={getAddressValue(type, "pincode")}
                  onChange={e => { if (/^\d*$/.test(e.target.value) && e.target.value.length <= 6) setAddressField(type, "pincode", e.target.value); }}
                  disabled={disabled}
                />
                {required && <FE error={errors?.pincode} />}
              </Col>
            </Row>
            <Row gutter={[12, 0]} style={{ marginTop: 10 }}>
              <Col xl={8} md={12} sm={24}>
                <FL>Address Line 1{required && " *"}</FL>
                <Input.TextArea
                  placeholder="Address line 1" rows={2} style={{ borderRadius: 7, textTransform: "uppercase", resize: "none" }}
                  value={getAddressValue(type, "addressLine1")}
                  onChange={e => setAddressField(type, "addressLine1", e.target.value)}
                  disabled={disabled}
                />
                {required && <FE error={errors?.addressLine1} />}
              </Col>
              <Col xl={8} md={12} sm={24}>
                <FL>Address Line 2{required && " *"}</FL>
                <Input.TextArea
                  placeholder="Address line 2" rows={2} style={{ borderRadius: 7, textTransform: "uppercase", resize: "none" }}
                  value={getAddressValue(type, "addressLine2")}
                  onChange={e => setAddressField(type, "addressLine2", e.target.value)}
                  disabled={disabled}
                />
                {required && <FE error={errors?.addressLine2} />}
              </Col>
              <Col xl={8} md={12} sm={24}>
                <FL>Address Line 3{required && " *"}</FL>
                <Input.TextArea
                  placeholder="Address line 3" rows={2} style={{ borderRadius: 7, textTransform: "uppercase", resize: "none" }}
                  value={getAddressValue(type, "addressLine3")}
                  onChange={e => setAddressField(type, "addressLine3", e.target.value)}
                  disabled={disabled}
                />
                {required && <FE error={errors?.addressLine3} />}
              </Col>
            </Row>
          </div>
        ),
      }]}
    />
  );
}

/* ── Main component ─────────────────────────────────────────────────────────── */
const VENDOR_CATEGORIES = [
  "PARTNERSHIP", "PROPRIETORYSHIP", "Limited Liability Partnership (LLP)",
  "PUBLIC LTD", "PVT LTD", "Foreign Limited", "Hindu Undivided Family (HUF)",
  "Society", "Club", "Trust", "Association of Persons (AOP) / Body of Individuals (BOI)",
  "Local Authority / Government Department / Statutory Body", "Co Operative Society",
];

export const GeneralInfoForm = () => {
  const { themeColor } = useThemeStore();
  const rgb = hexToRgb(themeColor);

  const {
    state, getState, city, getCity, getDivision, division,
    getVendorDetailByGuid, vendorDetailByGuid,
    loading: actionLoading, checkBoxes, setCheckbox,
  } = useVendorMasterStore();
  const setAllCheckboxes = useVendorMasterStore(s => s.setAllCheckboxes);

  const GUID      = localStorage.getItem("vendorGUID");
  const submitted = localStorage.getItem("submitted") === "Yes";
  const approve   = localStorage.getItem("showApprove") === "true";
  const showStep4 = localStorage.getItem("ShowStep4") === "Yes";
  const { VendorType } = vendorDetailByGuid?.Vendor || {};

  const {
    formData, errors, setField, setAddressField,
    copyAddress, setPanError, clearAddress,
  } = useGenralInfoFormStore();

  const [copyinOA,     setCopyInOa]     = useState(false);
  const [copyInIa,     setCopyInIa]     = useState(false);
  const [copyInRa,     setCopyInRa]     = useState(false);
  const [copyInRta,    setCopyInRta]    = useState(false);
  const [country,      setCountry]      = useState([]);
  const [open,         setOpen]         = useState(false);
  const [panVerified,  setPanVerified]  = useState(false);
  const [imageUrl,     setImageUrl]     = useState(null);
  const [fileName,     setFileName]     = useState("");
  const [fileType,     setFileType]     = useState("");
  const [loading,      setLoading]      = useState(false);
  const [vendorType,   setVendorType]   = useState([]);

  useEffect(() => { fetchType(); getState(); fetchCountry(); getDivision(); }, []);
  useEffect(() => { if (GUID) getVendorDetailByGuid(GUID); }, [GUID]);
  useEffect(() => { if (!approve) setAllCheckboxes(true); }, [approve, setAllCheckboxes]);
  useEffect(() => {
    if (errors?.panNumber === undefined) setPanVerified(true);
    if (formData?.panNumber === "") setPanVerified(false);
  }, [errors?.panNumber, formData?.panNumber]);

  async function fetchType() {
    try {
      setLoading(true);
      const res = await getTradeTypeApi();
      setVendorType(res?.Data ?? []);
    } catch { message.error("Failed to load vendor types!"); }
    finally  { setLoading(false); }
  }

  async function fetchCountry() {
    const val = await apiRequest("GET", "Country");
    setCountry(val?.IsSuccess && Array.isArray(val.Data) ? val.Data : []);
  }

  function getAddressValue(addressType, field) {
    const addr = formData.addresses.find(a => a.addressType === addressType);
    return addr ? addr[field] : "";
  }

  async function validatePan(value) {
    const pan = value.toUpperCase();
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(pan)) {
      setPanVerified(false); setPanError("Invalid PAN Format"); return;
    }
    try {
      const res     = await panValidationApi(pan);
      const status  = res?.Data?.Status;
      const cStatus = res?.Data?.Data?.ClientStatus;
      if (status === "SUCCESS" && cStatus !== "Invalid") {
        setPanError(""); setPanVerified(true); message.success(cStatus);
      } else {
        setPanVerified(false); setPanError("PAN is not valid");
        message.error(cStatus || "PAN is not valid");
      }
    } catch { setPanVerified(false); setPanError("PAN validation failed"); }
  }

  const allChecked = Object.values(checkBoxes).every(Boolean);

  /* ── Copy toggle helpers ────────────────────────────────────────────────── */
  function handleCopy(type, checked, setCopied) {
    setCopied(checked);
    checked ? copyAddress(type) : clearAddress(type);
  }

  /* ── Address block common props ─────────────────────────────────────────── */
  const addrProps = { themeColor, country, state, getAddressValue, setAddressField, getCity, disabled: submitted, errors };

  return (
    <>
      {/* Global loading overlay */}
      {loading && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(255,255,255,0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
          <Spin size="large" />
        </div>
      )}

      {/* ── Section 1: General Information ───────────────────────────────── */}
      <Collapse
        defaultActiveKey={["1"]}
        style={{ borderRadius: 10, border: "1px solid #f0f1f3", overflow: "hidden" }}
        items={[{
          key: "1",
          label: (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <InfoCircleOutlined style={{ color: themeColor, fontSize: 13 }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>General Information</span>
            </div>
          ),
          children: (
            <div style={{ padding: "4px 0" }}>
              <Row gutter={[12, 0]}>
                {/* PAN Number */}
                <Col xl={5} md={12} sm={24}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                    <FL>PAN Number <span style={{ color: "#ef4444" }}>*</span></FL>
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <CheckCircleFilled style={{ fontSize: 16, color: panVerified ? "#16a34a" : "#e5e7eb", transition: "color 0.3s" }} />
                      {panVerified && <span style={{ fontSize: 10, fontWeight: 700, color: "#16a34a" }}>Verified</span>}
                    </div>
                  </div>
                  <Input
                    placeholder="e.g. ABCDE1234F"
                    style={{ borderRadius: 7, height: 34, fontFamily: "monospace", fontWeight: 600 }}
                    value={formData.panNumber}
                    onChange={e => setField("panNumber", e.target.value.toUpperCase())}
                    onBlur={e => validatePan(e.target.value)}
                    disabled={submitted}
                    status={errors?.panNumber ? "error" : panVerified ? "" : ""}
                  />
                  <FE error={errors?.panNumber} />
                </Col>

                {/* Vendor Name */}
                <Col xl={7} md={12} sm={24}>
                  <FL>Vendor Name <span style={{ color: "#ef4444" }}>*</span></FL>
                  <Input
                    placeholder="Enter vendor name" style={{ borderRadius: 7, height: 34 }}
                    value={formData?.vendorName}
                    onChange={e => setField("vendorName", e.target.value)}
                    disabled
                  />
                  <FE error={errors?.vendorName} />
                </Col>

                {/* Vendor Category */}
                <Col xl={6} md={12} sm={24}>
                  <FL>Vendor Category <span style={{ color: "#ef4444" }}>*</span></FL>
                  <Select
                    placeholder="Select category" allowClear showSearch optionFilterProp="label"
                    style={{ width: "100%", height: 34 }}
                    options={VENDOR_CATEGORIES.map(c => ({ label: c, value: c }))}
                    value={formData.businessCategory}
                    onChange={v => setField("businessCategory", v)}
                    disabled={submitted}
                  />
                  <FE error={errors?.businessCategory} />
                </Col>

                {/* Vendor Type */}
                <Col xl={3} md={12} sm={24}>
                  <FL>Vendor Type <span style={{ color: "#ef4444" }}>*</span></FL>
                  <Select
                    placeholder="Select type" allowClear showSearch optionFilterProp="label"
                    style={{ width: "100%", height: 34 }}
                    options={vendorType?.map(t => ({ label: t.VendorTypeName, value: t.VendorTypeCode }))}
                    value={formData?.vendorType}
                    onChange={v => setField("vendorType", v)}
                    disabled
                  />
                  <FE error={errors?.vendorType} />
                </Col>

                {/* Vendor Division (non-ZAEX only) */}
                {VendorType !== "ZAEX" && (
                  <Col xl={3} md={12} sm={24}>
                    <FL>Division <span style={{ color: "#ef4444" }}>*</span></FL>
                    <Select
                      placeholder="Select division" allowClear showSearch optionFilterProp="label"
                      style={{ width: "100%", height: 34 }}
                      options={Array.isArray(division) ? division.map(d => ({ label: d.DivisionName, value: d.Division })) : []}
                      value={formData.vendorDivision}
                      onChange={v => setField("vendorDivision", v)}
                      disabled
                    />
                    <FE error={errors?.vendorDivision} />
                  </Col>
                )}

                {/* Approval checkbox (admin review mode) */}
                {submitted && showStep4 && (
                  <Col xl={2} md={6} sm={12} style={{ display: "flex", alignItems: "flex-end", paddingBottom: 4 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 10px", borderRadius: 8, border: `1px solid rgba(${rgb},0.20)`, background: `rgba(${rgb},0.05)` }}>
                      <Checkbox
                        checked={checkBoxes.generalInfo}
                        disabled={!approve}
                        onChange={() => setCheckbox("generalInfo", !checkBoxes.generalInfo)}
                      />
                      <span style={{ fontSize: 11, fontWeight: 700, color: themeColor }}>Approved</span>
                    </div>
                  </Col>
                )}
              </Row>
            </div>
          ),
        }]}
      />

      {/* ── Address blocks ───────────────────────────────────────────────── */}
      <AddressBlock
        type="REGISTERED_HO" label="Registered HO" required
        showApprovalCheck={submitted && showStep4}
        approvalChecked={checkBoxes.registeredHo}
        onApprovalChange={() => setCheckbox("registeredHo", !checkBoxes.registeredHo)}
        approveEnabled={approve}
        {...addrProps}
      />

      <AddressBlock
        type="Order_Address" label="Order Address"
        copyable copyChecked={copyinOA}
        onCopyChange={checked => handleCopy("Order_Address", checked, setCopyInOa)}
        {...addrProps}
      />

      {VendorType !== "ZAEX" && (
        <>
          <AddressBlock
            type="Invoice_Address" label="Invoice Address"
            copyable copyChecked={copyInIa}
            onCopyChange={checked => handleCopy("Invoice_Address", checked, setCopyInIa)}
            {...addrProps}
          />
          <AddressBlock
            type="Remitance_Address" label="Remittance Address"
            copyable copyChecked={copyInRa}
            onCopyChange={checked => handleCopy("Remitance_Address", checked, setCopyInRa)}
            {...addrProps}
          />
          <AddressBlock
            type="Return_Address" label="Return Address"
            copyable copyChecked={copyInRta}
            onCopyChange={checked => handleCopy("Return_Address", checked, setCopyInRta)}
            {...addrProps}
          />
        </>
      )}

      {/* ── Approve / Reject bar (admin mode) ───────────────────────────── */}
      {submitted && approve && <ApproveReject checked={allChecked} />}

      {/* Image preview modal */}
      {open && (
        <ImagePreviewModal
          open={open}
          onClose={() => { setOpen(false); setImageUrl(null); setFileName(""); setFileType(""); }}
          imageUrl={imageUrl} fileName={fileName} fileType={fileType}
        />
      )}
    </>
  );
};
